import { useTranslation } from "react-i18next";
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  NumberInput,
  Select,
  SelectItem,
  TextArea,
  TextInput,
} from "@carbon/react";
import {
  CURRENCY_OPTIONS,
  PurchaseRequestItemType,
  PurchaseRequestItemV2,
} from "../../../types";

export interface EditItemModalProps {
  item: PurchaseRequestItemV2 | null;
  globalCurrency: string;
  isBusy: boolean;
  canEditRequest: boolean;
  onChange: (key: keyof PurchaseRequestItemV2, value: unknown) => void;
  onSave: () => void;
  onDelete: () => void;
  onClose: () => void;
}

/**
 * The per-item edit modal (Task 5) — Carbon `ComposedModal` in place of the pre-Carbon custom
 * `Modal`. Same fields (quantity, description, budget currency + value, and — for SERVICE items —
 * purpose and scope/application), the same delete/cancel/save affordances, and the same
 * `budgetCurrency`/`currency` twin-write on the currency select.
 */
const EditItemModal = ({
  item,
  globalCurrency,
  isBusy,
  canEditRequest,
  onChange,
  onSave,
  onDelete,
  onClose,
}: EditItemModalProps) => {
  const { t } = useTranslation();

  return (
    <ComposedModal
      open={!!item}
      onClose={onClose}
      aria-label={t("purchaseRequests.items.editTitle")}
    >
      <ModalHeader title={t("purchaseRequests.items.editTitle")} />
      <ModalBody>
        {item ? (
          <div className="pr-detail__edit-form">
            <NumberInput
              id="edit-item-quantity"
              label={t("purchaseRequests.items.columns.quantity")}
              value={item.quantity ?? ""}
              onChange={(_event, { value }) => onChange("quantity", value)}
              hideSteppers
            />

            <TextArea
              id="edit-item-description"
              labelText={t("purchaseRequests.items.columns.description")}
              rows={3}
              value={item.description || ""}
              onChange={(event) => onChange("description", event.target.value)}
            />

            <div className="pr-detail__edit-budget">
              <Select
                id="edit-item-currency"
                labelText={t("purchaseRequests.budgetType.currency", {
                  defaultValue: "Currency",
                })}
                value={item.budgetCurrency || item.currency || globalCurrency || ""}
                onChange={(event) => {
                  const value = event.target.value || null;
                  onChange("currency", value);
                  onChange("budgetCurrency", value);
                }}
              >
                {CURRENCY_OPTIONS.map((option) => (
                  <SelectItem
                    key={option.value}
                    value={option.value}
                    text={option.label}
                  />
                ))}
              </Select>

              <NumberInput
                id="edit-item-budget"
                label={t("purchaseRequests.items.columns.budgetValue")}
                value={item.budgetValue ?? ""}
                onChange={(_event, { value }) =>
                  onChange(
                    "budgetValue",
                    value !== "" && value != null ? Number(value) : null
                  )
                }
                hideSteppers
              />
            </div>

            {item.itemType === PurchaseRequestItemType.SERVICE ? (
              <>
                <TextInput
                  id="edit-item-purpose"
                  labelText={`${t("purchaseRequests.items.columns.purpose")} *`}
                  maxLength={1000}
                  value={item.purpose || ""}
                  onChange={(event) => onChange("purpose", event.target.value)}
                />
                <TextInput
                  id="edit-item-application"
                  labelText={t("purchaseRequests.items.columns.application")}
                  maxLength={1000}
                  value={item.brand || ""}
                  onChange={(event) => onChange("brand", event.target.value)}
                />
              </>
            ) : null}
          </div>
        ) : null}
      </ModalBody>
      <ModalFooter>
        <Button
          kind="danger"
          onClick={onDelete}
          disabled={isBusy || !canEditRequest}
        >
          {t("cta.delete")}
        </Button>
        <Button kind="secondary" onClick={onClose} disabled={isBusy}>
          {t("cta.cancel")}
        </Button>
        <Button
          kind="primary"
          onClick={onSave}
          disabled={isBusy || !canEditRequest}
        >
          {t("cta.save")}
        </Button>
      </ModalFooter>
    </ComposedModal>
  );
};

export default EditItemModal;
